/*
 * 文件职责 / File Purpose
 * - 中文：提供研究名单生命周期标签、状态显示、响应解包和纯展示格式化规则。
 * - English: Provides lifecycle tabs, status presentation, response unwrapping, and pure display formatting for research watchlists.
 *
 * 事实边界 / Fact Boundary
 * - 中文：状态和数量必须来自 BFF 投影；本文件只规范显示，不创建 TrackingRecord、CoverageCase 或报告事实。
 * - English: Status and counts must come from BFF projections; this module formats display values and creates no TrackingRecord, CoverageCase, or report fact.
 */
export const WATCHLIST_TABS = [
  { key: 'tracking', label: '持续跟踪' },
  { key: 'nominations', label: '待首报' },
  { key: 'coverage', label: '首报进行中' },
  { key: 'decision', label: '待确认' },
  { key: 'history', label: '历史' },
];

export const TAB_KEYS = new Set(WATCHLIST_TABS.map(item => item.key));
export const MODE_KEYS = new Set(['research_board', 'table']);

export const unwrapResponse = response => {
  if (response?.success === false) {
    const error = new Error(response?.error?.message || '研究工作台请求失败');
    error.code = response?.error?.code;
    error.details = response?.error?.details;
    throw error;
  }
  return response?.data ?? response;
};

export const getOwnerId = item => (
  item?.trackingRecordId
  || item?.ownerId
  || item?.currentCaseId
  || item?.coverageCaseId
  || item?.id
);

export const getCoverageCaseId = item => (
  item?.coverageCaseId || item?.currentCaseId || null
);

export const getItemStatus = item => (
  item?.lifecycleStatus || item?.status || item?.reportStatus || 'unknown'
);

export const getItemKey = item => [
  item?.ownerType || 'research',
  getOwnerId(item) || item?.symbol,
  item?.symbol,
].filter(Boolean).join(':');

export const RESEARCH_BOUNDARY_TEXT = '截至研究截止日未取得可用于该项判断的材料';
export const TECHNICAL_ISSUE_TEXT = '研究执行或材料校验未完成；技术详情见审计记录';

export const RESEARCH_BOUNDARY_CAUSES = new Set([
  'DATA_GAP',
  'NOT_APPLICABLE',
  'NOT_TRIGGERED',
]);

const RESEARCH_BOUNDARY_PRESENTATION = {
  DATA_GAP: { color: 'default', label: RESEARCH_BOUNDARY_TEXT },
  NOT_APPLICABLE: { color: 'default', label: '该项不适用于本公司或本期研究' },
  NOT_TRIGGERED: { color: 'default', label: '截至研究截止日，该项条件未触发' },
};

export const researchBoundaryCauseOf = value => {
  if (!value || typeof value !== 'object') return '';
  const cause = String(
    value.researchBoundaryCause
    || value.research_boundary_cause
    || value.boundaryCause
    || value.boundary_cause
    || value.researchBoundary?.cause
    || ''
  ).trim().toUpperCase();
  return RESEARCH_BOUNDARY_CAUSES.has(cause) ? cause : '';
};

export const researchBoundaryMeta = value => (
  RESEARCH_BOUNDARY_PRESENTATION[researchBoundaryCauseOf(value)] || null
);

const STATUS_PRESENTATION = {
  active: { color: 'success', label: '持续跟踪' },
  tracking_active: { color: 'success', label: '持续跟踪' },
  ready: { color: 'success', label: '已形成' },
  succeeded: { color: 'success', label: '已完成' },
  completed: { color: 'success', label: '已完成' },
  accepted: { color: 'success', label: '已验收' },
  submitted: { color: 'success', label: '已提交' },
  failed: { color: 'error', label: '研究执行未完成' },
  report_failed: { color: 'error', label: '研究执行未完成' },
  generation_failed: { color: 'error', label: '研究执行未完成' },
  blocked: { color: 'error', label: '材料传递或校验未通过' },
  quality_blocked: { color: 'error', label: '材料传递或校验未通过' },
  stale: { color: 'warning', label: '材料新鲜度需复核' },
  evidence_pending: { color: 'warning', label: '已形成，待复核' },
  decision_pending: { color: 'warning', label: '待人工决定' },
  report_pending: { color: 'processing', label: '待生成首报' },
  draft: { color: 'default', label: '研究草稿' },
  pending: { color: 'default', label: '待处理' },
  queued: { color: 'processing', label: '排队中' },
  generating: { color: 'processing', label: '生成中' },
  running: { color: 'processing', label: '执行中' },
  partial: { color: 'error', label: TECHNICAL_ISSUE_TEXT },
  degraded: { color: 'error', label: TECHNICAL_ISSUE_TEXT },
  unknown: { color: 'warning', label: '技术状态见审计记录' },
};

const UNCLASSIFIED_ABSENCE_STATES = new Set([
  'missing',
  'not_available',
  'not_observable',
  'insufficient_evidence',
  'not_applicable',
  'not_triggered',
]);

const INTAKE_SOURCE_LABELS = {
  strategy8_first_alert: '策略8首次预警',
  strategy9_first_alert: '策略9首次突破',
  business_nomination: '业务人工提名',
};

const safeScalar = value => (
  /^\d+(?:\.\d+)?(?:\s*\/\s*\d+)?(?:%|项|只|份|轮)?$/u.test(value)
  || /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/u.test(value)
  || /^\d{6}\.(?:SH|SZ|BJ)$/u.test(value)
);

export const businessPresentationText = value => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (Array.isArray(value)) {
    const items = value.map(businessPresentationText).filter(item => item !== '—');
    return items.join('；') || '—';
  }
  if (typeof value === 'object') {
    const boundaryMeta = researchBoundaryMeta(value);
    if (boundaryMeta) return boundaryMeta.label;
    return businessPresentationText(
      value.businessText || value.summary || value.message || value.reason || value.status || value.code
    );
  }
  const raw = String(value).trim();
  const normalized = raw.toLowerCase();
  if (UNCLASSIFIED_ABSENCE_STATES.has(normalized)) return TECHNICAL_ISSUE_TEXT;
  if (STATUS_PRESENTATION[normalized]) return STATUS_PRESENTATION[normalized].label;
  if (INTAKE_SOURCE_LABELS[normalized]) return INTAKE_SOURCE_LABELS[normalized];
  if (safeScalar(raw)) return raw;
  if ([ '\u964d\u7ea7', '有限可用', '部分可用', '部分完成', '部分支持' ].includes(raw)) {
    return TECHNICAL_ISSUE_TEXT;
  }
  if (raw.includes('\u964d\u7ea7') || /\bdegraded\b/i.test(raw)) return TECHNICAL_ISSUE_TEXT;
  if (
    /(?:^|\b)[a-z][a-z0-9]*(?:_[a-z0-9]+)+(?:\b|$)/iu.test(raw)
    || /\b(?:[A-Z][A-Z0-9_]{2,}|BFF|ModelRuntime|CoverageCase|ResearchCard)\b/u.test(raw)
    || /[{}[\]`]|\*\*/u.test(raw)
    || /失败|错误|异常|超时|连接拒绝/u.test(raw)
    || [ ...raw ].every(character => character.codePointAt(0) <= 127)
  ) {
    return TECHNICAL_ISSUE_TEXT;
  }
  return raw;
};

export const intakeSourceLabel = value => (
  INTAKE_SOURCE_LABELS[String(value || '').trim().toLowerCase()]
  || businessPresentationText(value)
);

export const statusMeta = (status, context = null) => {
  const boundaryMeta = researchBoundaryMeta(context || status);
  if (boundaryMeta) return boundaryMeta;
  const normalized = String(
    status && typeof status === 'object'
      ? status.status || status.state || 'unknown'
      : status || 'unknown'
  ).trim().toLowerCase();
  if (UNCLASSIFIED_ABSENCE_STATES.has(normalized)) {
    return { color: 'warning', label: TECHNICAL_ISSUE_TEXT };
  }
  return STATUS_PRESENTATION[normalized]
    || { color: 'warning', label: '技术状态见审计记录' };
};

export const formatValue = value => {
  if (value === 0) return '0';
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};
